import { Component, Vue, Prop, Ref, Watch } from 'vue-property-decorator';
import { State, Getter, Action } from 'vuex-class';
import { Camera } from '@/models/camera';
import { Project } from '@/models/project';
import { User } from '@/models/user';
import { Album } from '@/models/album';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';
import { Photo } from '@/models/photo';
import { AlbumCreateRequest } from '@/services/albums/albums.interfaces';
// @ts-ignore no d.ts
import VirtualCollection from 'vue-virtual-collection';
import PremiumOverlay from '../premiumoverlay/premiumoverlay.component.vue';
import S3Image from '../s3-image/S3Image.vue';
import { Logger } from 'aws-amplify';
import * as d3 from 'd3';
import * as d3Array from 'd3-array';
const logger = new Logger('AlbumComponent');

@Component({
  components: {
    S3Image,
    PremiumOverlay,
  },
})
export default class AlbumComponent extends Vue {
  @Ref('photos') public readonly photosRef!: HTMLDivElement;
  @Ref('scroller') public readonly scroller!: VirtualCollection;
  @Ref('createAlbumModal') public readonly createAlbumModal!: { show: () => void; hide: () => void };
  @Prop({ type: Object, required: true }) private readonly camera!: Camera;
  @Prop({ type: Object, required: true }) private readonly project!: Project;
  @Prop({ required: true }) readonly isPremium!: boolean;
  @Prop({ type: Object, required: true }) private readonly album!: Album;

  @State('loadingAlbum', { namespace: 'album' }) private readonly loadingAlbum!: boolean;
  @State('albums', { namespace: 'album' }) private readonly albums!: Album[];
  @State('user', { namespace: 'profile' }) private user!: User;

  @State('photos', { namespace: 'photo' }) private photos!: Photo[];
  @Getter('onlySlowmotion', { namespace: 'photo' }) private slowMotions!: Photo[];
  @Getter('projectWithId', { namespace: 'projects' }) private projectWithId!: (id: string) => Project | undefined;

  @Action('loadOneAlbum', { namespace: 'album' }) private loadOneAlbum!: (albumId: string) => Promise<Album>;
  @Action('loadAlbum', { namespace: 'album' }) private loadAlbum!: (payload: {
    projectId: string;
    cameraId: string;
  }) => Promise<Album[]>;
  @Action('createNewAlbum', { namespace: 'album' }) private readonly createNewAlbum!: (albumCreateRequest: {
    cameraId: string;
    projectId: string;
    name: string;
  }) => Promise<Album[]>;
  @Action('findAllFor', { namespace: 'photo' }) private findPhotoAllForProject!: (payload: {
    projectId: string;
    cameraId: string;
  }) => Promise<Photo[]>;

  private visible = true;

  private scrollerWidth = 0;
  private scrollerColumns = 0;
  private scrollerImageWidth = 0;
  private items: { data: Album }[] = [];
  private newAlbumName = '';
  private s3Photos: Array<any> = [];
  private lastActivityScore: number | null = null;
  private leftActivityScore: number | null = null;
  private lastPersonScore: number | null = null;
  private leftPersonScore: number | null = null;
  private lastDensityScore: number | null = null;
  private leftDensityScore: number | null = null;
  private lastHelmetScore: number | null = null;
  private leftHelmetScore: number | null = null;
  private selectedTimeRange: number | string = 'all';

  private createAlbumRequest: AlbumCreateRequest = new AlbumCreateRequest();

  public get isAdmin(): boolean {
    return (
      this.user !== undefined &&
      (this.project.owner.id === this.user.id || this.project.administratorId.includes(this.user.id))
    );
  }

  public async mounted(): Promise<void> {
    await this.loadDataAlbum();
    if (this.albums.length > 0) {
      this.onAlbumsStoreChange(this.albums, []);
    }
    this.$nextTick(() => {
      window.addEventListener('resize', this.updatePhotosSize);
    });
  }

  public updatePhotosSize(): void {
    if (this.scroller) {
      const w = this.photosRef.clientWidth;
      this.scrollerColumns = Math.ceil(w / 150);
      this.scrollerWidth = w;
      this.scrollerImageWidth = (w - 10 - this.scrollerColumns * 5) / this.scrollerColumns;
      this.items = this.items.slice(0);
    }
  }

  private selectTimeRange(range: number | string): void {
    this.selectedTimeRange = range;
    this.loadKpiData(this.photos); // Recalculer les KPI avec la nouvelle plage de temps
  }

  private async loadDataAlbum(): Promise<void> {
    await this.loadAlbum({ projectId: this.project.id, cameraId: this.camera.id });
    const photos = await this.findPhotoAllForProject({ projectId: this.project.id, cameraId: this.camera.id });
    await this.loadKpiData(photos); // Move this here
  }

  private async loadKpiData(photos: Photo[]): Promise<void> {
    try {
      if (!photos || photos.length === 0) {
        console.warn('Aucune photo trouvée pour les KPI.');
        return; // Arrêter le traitement si aucune photo n'est trouvée
      }

      // Mapper les photos pour obtenir les données nécessaires
      const data = photos.map((photo) => ({
        date: new Date(photo.createdAt),
        toupie: Number(photo.detectedLabels?.toupie ?? 0),
        crane: Number(photo.detectedLabels?.crane ?? 0),
        nacelle: Number(photo.detectedLabels?.nacelle ?? 0),
        pelle: Number(photo.detectedLabels?.pelle ?? 0),
        person: Number(photo.detectedLabels?.Person ?? 0),
        benne: Number(photo.detectedLabels?.benne ?? 0),
        compateuse: Number(photo.detectedLabels?.compateuse ?? 0),
        gruemobile: Number(photo.detectedLabels?.gruemobile ?? 0),
        camion: Number(photo.detectedLabels?.camion ?? 0),
        foreuse: Number(photo.detectedLabels?.foreuse ?? 0),
        helmet: Number(photo.detectedLabels?.helmet ?? 0),
        echelle: Number(photo.detectedLabels?.echelle ?? 0),
        goutte: Number(photo.detectedLabels?.goutte ?? 0),
        density: Number(photo.detectedLabels?.density ?? 0),
        unknown: Number(photo.detectedLabels?.Unknown ?? 0),
      }));

      // Trier les données par date
      data.sort((a, b) => a.date.getTime() - b.date.getTime());

      // Filtrer les données selon la période sélectionnée
      let filteredData = data;
      if (this.selectedTimeRange !== 'all') {
        const now = new Date();
        const cutoffDate = new Date(now.getTime() - Number(this.selectedTimeRange) * 24 * 60 * 60 * 1000);
        filteredData = data.filter((d) => d.date >= cutoffDate);
      }

      if (filteredData.length === 0) {
        console.warn('Aucune donnée dans la période sélectionnée.');
        // Réinitialiser les KPI
        this.lastActivityScore = null;
        this.leftActivityScore = null;
        this.lastPersonScore = null;
        this.leftPersonScore = null;
        this.lastHelmetScore = null;
        this.leftHelmetScore = null;
        this.lastDensityScore = null;
        this.leftDensityScore = null;
        return;
      }

      // Préparer les données
      const preparedData = this.prepareData(filteredData);

      // Calculer les KPI
      this.calculateSecondHalfAverage(preparedData);
    } catch (error) {
      console.error('Erreur lors du chargement des données pour les KPI:', error);
    }
  }

  private prepareData(data: any[]) {
    // Définir la plage de dates en millisecondes
    const xRange = [data[0].date.getTime(), data[data.length - 1].date.getTime()];

    // Filtrer les données en fonction de la plage de dates
    const filteredData = data.filter((d) => {
      const time = d.date.getTime();
      return time >= xRange[0] && time <= xRange[1];
    });

    const dataPointCount = filteredData.length;

    // Calcul de la durée de la plage visible (en millisecondes)
    const rangeDuration = xRange[1] - xRange[0];
    const dayDuration = 24 * 60 * 60 * 1000; // Durée d'un jour en millisecondes
    const weekDuration = 7 * dayDuration; // Durée d'une semaine en millisecondes

    // Seuils pour les différents niveaux d'agrégation
    const shouldAggregateByWeek = rangeDuration > 21 * weekDuration; // Agrégation par semaine si plage > 4 semaines
    const shouldAggregateByDay = rangeDuration > 7 * dayDuration && !shouldAggregateByWeek; // Agrégation par jour si > 2 jours

    let xData: Date[] = [];
    let aggregatedData: { [key: string]: number[] } = {};

    // Définir les catégories à analyser
    const categories = [
      { key: 'toupie', label: 'Toupie' },
      { key: 'crane', label: 'Grue' },
      { key: 'nacelle', label: 'Nacelle' },
      { key: 'pelle', label: 'Pelle' },
      { key: 'person', label: 'Personne' },
      { key: 'benne', label: 'Benne' },
      { key: 'compateuse', label: 'Compateuse' }, // Orthographe alignée
      { key: 'gruemobile', label: 'Grue Mobile' }, // Clé alignée avec 'gruemobile'
      { key: 'camion', label: 'Camion' },
      { key: 'foreuse', label: 'Foreuse' },
      { key: 'helmet', label: 'Casque' },
      { key: 'echelle', label: 'Échelle' },
      { key: 'goutte', label: 'Goutte' },
      { key: 'density', label: 'Densité' },
      { key: 'unknown', label: 'Inconnu' },
    ];

    if (dataPointCount <= 2) {
      // Utiliser les données non agrégées si 2 points ou moins
      xData = filteredData.map((d) => d.date);
      aggregatedData = {};
      categories.forEach((category) => {
        aggregatedData[category.key] = filteredData.map((d) => d[category.key]);
      });
    } else if (shouldAggregateByWeek) {
      // Agrégation par semaine
      const groupedData = d3Array.groups(filteredData, (d) => d3.timeWeek(d.date));

      xData = [];
      aggregatedData = {};
      categories.forEach((category) => {
        aggregatedData[category.key] = [];
      });

      groupedData.forEach(([dateStr, items]) => {
        const date = new Date(dateStr);
        xData.push(date);

        categories.forEach((category) => {
          const total = items.reduce((sum, d) => sum + d[category.key], 0) / items.length;
          aggregatedData[category.key].push(total);
        });
      });
    } else if (shouldAggregateByDay) {
      // Agrégation par jour
      const groupedData = d3Array.groups(filteredData, (d) => d3.timeDay(d.date));

      xData = [];
      aggregatedData = {};
      categories.forEach((category) => {
        aggregatedData[category.key] = [];
      });

      groupedData.forEach(([dateStr, items]) => {
        const date = new Date(dateStr);
        xData.push(date);

        categories.forEach((category) => {
          const total = items.reduce((sum, d) => sum + d[category.key], 0) / items.length;
          aggregatedData[category.key].push(total);
        });
      });
    } else {
      // Afficher chaque point sans agrégation
      xData = filteredData.map((d) => d.date);
      aggregatedData = {};
      categories.forEach((category) => {
        aggregatedData[category.key] = filteredData.map((d) => d[category.key]);
      });
    }

    return { xData, aggregatedData, categories };
  }

  private calculateSecondHalfAverage(preparedData: any) {
    const { xData, aggregatedData, categories } = preparedData;

    if (xData.length === 0) {
      console.warn('Aucune donnée disponible pour le calcul des moyennes.');
      return;
    }

    const halfIndex = Math.floor(xData.length / 2);

    // Calcul pour la seconde moitié des données
    const secondHalfYData = [];
    for (let i = halfIndex; i < xData.length; i++) {
      const sum = categories.reduce((total: number, { key }: { key: string }) => total + aggregatedData[key][i], 0);
      secondHalfYData.push(sum);
    }

    // Calcul pour la première moitié des données
    const firstHalfYData = [];
    for (let i = 0; i < halfIndex; i++) {
      const sum = categories.reduce((total: number, { key }: { key: string }) => total + aggregatedData[key][i], 0);
      firstHalfYData.push(sum);
    }

    // Calculer les moyennes
    this.lastActivityScore = this.calculateAverage(secondHalfYData);
    this.leftActivityScore = this.calculateAverage(firstHalfYData);

    // Calcul pour les personnes détectées
    const personValues = aggregatedData['person'];
    const firstHalfPersonData = personValues.slice(0, halfIndex);
    const secondHalfPersonData = personValues.slice(halfIndex);

    this.lastPersonScore = this.calculateAverage(secondHalfPersonData);
    this.leftPersonScore = this.calculateAverage(firstHalfPersonData);

    // Calcul pour le Ratio Casque/Personne
    const helmetValues = aggregatedData['helmet'];
    const firstHalfHelmetData = helmetValues.slice(0, halfIndex);
    const secondHalfHelmetData = helmetValues.slice(halfIndex);

    this.lastHelmetScore = this.calculateRatio(secondHalfHelmetData, secondHalfPersonData);
    this.leftHelmetScore = this.calculateRatio(firstHalfHelmetData, firstHalfPersonData);

    // Calcul pour la densité
    const densityValues = aggregatedData['density'];
    const firstHalfDensityData = densityValues.slice(0, halfIndex);
    const secondHalfDensityData = densityValues.slice(halfIndex);

    this.lastDensityScore = this.calculateAverage(secondHalfDensityData);
    this.leftDensityScore = this.calculateAverage(firstHalfDensityData);
  }

  private calculateAverage(values: number[]): number {
    if (values.length === 0) {
      console.warn('Aucune valeur pour le calcul de la moyenne.');
      return 0;
    }
    const total = values.reduce((sum, value) => sum + value, 0);
    return total / values.length;
  }

  private calculateRatio(numeratorData: number[], denominatorData: number[]): number {
    const totalNumerator = numeratorData.reduce((sum, value) => sum + value, 0);
    const totalDenominator = denominatorData.reduce((sum, value) => sum + value, 0);
    return totalDenominator > 0 ? (totalNumerator / totalDenominator) * 100 : 0;
  }

  public cellSizeAndPositionGetter(item: Photo, index: number): unknown {
    // compute size and position
    const w = this.scrollerImageWidth + 5;
    const h = this.scrollerImageWidth;
    return {
      width: w,
      height: h,
      x: (index % this.scrollerColumns) * w,
      y: Math.floor(index / this.scrollerColumns) * h,
    };
  }

  public countPhotos(photos: Array<any>): number {
    return photos.filter((photo) => !photo.key.endsWith('.mp4')).length;
  }

  public countVideos(photos: Array<any>): number {
    return photos.filter((photo) => photo.key.endsWith('.mp4')).length;
  }

  @Watch('albums')
  public onAlbumsStoreChange(val: Album[], oldVal: Album[]): void {
    logger.info('onAlbumsStoreChange', { val, oldVal });
    this.updateAlbum();
  }

  @Watch('photos', { immediate: true })
  private onPhotosChange(val: Photo[], oldVal: Photo[]): void {
    if (val && val.length > 0) {
      this.loadKpiData(val);
    }
  }

  public onClick(albumId: string): void {
    logger.info('onClick', { albumId });
    if (albumId === 'slowmotion') {
      this.$router.push({ name: 'slowmotionview', params: { cameraid: this.camera.id, projectid: this.project.id } });
    } else {
      this.$router.push({ name: 'albumview', params: { id: albumId } });
    }
  }

  private updateAlbum() {
    this.items = this.albums.map((a) => ({ data: a }));
    if (this.slowMotions.length > 0) {
      this.items.push({
        data: {
          cameraId: this.camera.id,
          projectId: this.project.id,
          name: 'Video',
          id: 'slowmotion',
          photos: this.slowMotions,
        },
      });
    }
    this.items.sort((a, b) => a.data.name.localeCompare(b.data.name));
    this.$nextTick(() => {
      this.updatePhotosSize();
    });
  }

  public showCreateAlbumModal(): void {
    // Vérification si la référence 'createAlbumModal' est définie
    if (this.createAlbumModal && typeof this.createAlbumModal.show === 'function') {
      this.createAlbumModal.show();
    } else {
      console.error("La référence 'createAlbumModal' n'est pas disponible.");
    }
  }

  public async createAlbum(): Promise<void> {
    if (this.newAlbumName.trim() === '') {
      alert("Le nom de l'album ne peut pas être vide.");
      return;
    }

    try {
      // Créer un objet AlbumCreateRequest
      this.createAlbumRequest.name = this.newAlbumName;
      this.createAlbumRequest.projectId = this.project.id;
      this.createAlbumRequest.cameraId = this.camera.id;
      this.createAlbumRequest.photoId = ''; // Assurez-vous de remplir cela ou gérez-le comme optionnel si nécessaire

      // Log pour vérifier les données envoyées
      console.log("Données envoyées à l'API :", JSON.stringify(this.createAlbumRequest, null, 2));

      // Appel à l'API pour créer un nouvel album
      const albums = await this.createNewAlbum(this.createAlbumRequest);

      if (albums.length > 0) {
        this.$emit('updateAlbumList', albums); // Mettre à jour la liste des albums
      }

      this.newAlbumName = '';
      if (this.createAlbumModal && typeof this.createAlbumModal.hide === 'function') {
        this.createAlbumModal.hide();
      } else {
        console.error("La référence 'createAlbumModal' n'est pas disponible.");
      }
    } catch (error) {
      logger.error("Erreur lors de la création de l'album :", error);
      alert("Une erreur s'est produite lors de la création de l'album.");
    }
  }
}
